// G3c: Triage queue — lets staff review, accept or reject pending intake submissions.
//
// Props:
//   projectId  — the current project
//   canEdit    — when false, action buttons are hidden (view-only users see the list only)
//   onAccepted — optional callback fired after an accept decision so the caller can
//               refresh the normal task list (accepted tasks become visible there)

import { useEffect, useState } from "preact/hooks";
import { CheckCircle, Inbox, XCircle } from "lucide-preact";
import type { Task } from "@juuplan/shared";
import { listTriage, decideTriage } from "../lib/api";

interface Props {
  projectId: number;
  canEdit: boolean;
  /** Called after a successful triage decision — callers should refresh their task lists. */
  onDecision?: () => void;
}

/**
 * Renders the pending intake-submission queue for a project.
 * Each entry shows title, description / intake summary, and creation date.
 * Editors get "Übernehmen" (accept) and "Ablehnen" (reject) buttons.
 */
export function TriageQueue(props: Props): preact.JSX.Element {
  const { projectId, canEdit, onDecision } = props;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Map of taskId → "accepting" | "rejecting" | null for per-row loading state.
  const [busy, setBusy] = useState<Record<number, "accepting" | "rejecting" | null>>({});

  const load = (): void => {
    setLoading(true);
    setError(null);
    listTriage(projectId)
      .then((rows) => {
        setTasks(rows);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Fehler beim Laden";
        setError(msg);
        setLoading(false);
      });
  };

  // Reload whenever the projectId changes.
  useEffect(load, [projectId]);

  const decide = (taskId: number, decision: "accept" | "reject"): void => {
    setBusy((prev) => ({ ...prev, [taskId]: decision === "accept" ? "accepting" : "rejecting" }));
    decideTriage(taskId, decision)
      .then(() => {
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
        setBusy((prev) => ({ ...prev, [taskId]: null }));
        onDecision?.();
      })
      .catch(() => {
        // api() toastet bereits; hier nur die Zeile aus dem "läuft"-Zustand holen.
        setBusy((prev) => ({ ...prev, [taskId]: null }));
      });
  };

  if (loading) {
    return (
      <div class="space-y-2 py-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            class="h-16 rounded-lg bg-[color:var(--color-surface-container-low)] animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div class="rounded-lg border border-[color:var(--color-error)] bg-[color:var(--color-error-container,#FEF2F2)] px-4 py-3 text-sm text-[color:var(--color-on-error-container,#991B1B)]">
        {error}
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div class="flex flex-col items-center justify-center gap-3 py-12 text-[color:var(--color-on-surface-variant)]">
        <Inbox size={24} aria-hidden="true" class="opacity-40" />
        <p class="text-sm">Keine Einträge zur Sichtung.</p>
      </div>
    );
  }

  return (
    <ul class="space-y-2" role="list" aria-label="Sichtungsliste">
      {tasks.map((task) => {
        const rowBusy = busy[task.id];
        // Build a human-readable summary from intake custom_values if present.
        const intakeSummary = buildIntakeSummary(task.custom_values);

        return (
          <li
            key={task.id}
            class="flex flex-col gap-2 rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-3 sm:flex-row sm:items-start sm:gap-4"
          >
            {/* Task info */}
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-[color:var(--color-on-surface)] truncate">
                {task.title}
              </p>
              {intakeSummary ? (
                <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)] line-clamp-3 whitespace-pre-wrap">
                  {intakeSummary}
                </p>
              ) : task.description ? (
                <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)] line-clamp-3">
                  {task.description}
                </p>
              ) : null}
              <p class="mt-1.5 text-label text-[color:var(--color-on-surface-variant)] opacity-70">
                Eingereicht: {formatDate(task.created_at)}
              </p>
            </div>

            {/* Action buttons — only for editors */}
            {canEdit && (
              <div class="flex items-center gap-2 shrink-0 self-start sm:self-auto">
                <button
                  type="button"
                  disabled={rowBusy != null}
                  onClick={() => decide(task.id, "accept")}
                  class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-primary-container,#DBEAFE)] hover:text-[color:var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[30px] disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={`Aufgabe "${task.title}" übernehmen`}
                  title="Aufgabe übernehmen (als normale Aufgabe annehmen)"
                >
                  <CheckCircle size={14} aria-hidden="true" />
                  {rowBusy === "accepting" ? "…" : "Übernehmen"}
                </button>
                <button
                  type="button"
                  disabled={rowBusy != null}
                  onClick={() => decide(task.id, "reject")}
                  class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-error-container,#FEF2F2)] hover:text-[color:var(--color-error)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)] focus-visible:ring-offset-2 transition-colors min-h-[30px] disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={`Aufgabe "${task.title}" ablehnen`}
                  title="Aufgabe ablehnen (archivieren)"
                >
                  <XCircle size={14} aria-hidden="true" />
                  {rowBusy === "rejecting" ? "…" : "Ablehnen"}
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Formats an ISO date string as a locale date (e.g. "18.6.2026").
 */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  } catch {
    // Optionale Formatierung — bei kaputtem/ungültigem ISO-String lieber den
    // Rohwert zeigen als die ganze Zeile zum Absturz zu bringen.
    return iso;
  }
}

/**
 * Extracts a short human-readable summary from intake custom_values.
 * Intake forms store their fields under "__intake" key as an object
 * with label/value pairs, or as a flat key→value map.
 * Falls back to undefined when no useful intake data is found.
 */
function buildIntakeSummary(customValues: Record<string, unknown>): string | undefined {
  if (!customValues || typeof customValues !== "object") return undefined;

  // Check for __intake key (intake form submissions store data here)
  const intake = customValues["__intake"];
  if (intake && typeof intake === "object" && !Array.isArray(intake)) {
    const lines: string[] = [];
    for (const [k, v] of Object.entries(intake as Record<string, unknown>)) {
      if (v !== null && v !== undefined && v !== "") {
        lines.push(`${k}: ${String(v)}`);
      }
    }
    if (lines.length > 0) return lines.join("\n");
  }

  return undefined;
}
